// FILECONTENT_START src/common/components/Inputs/DatePicker/DatePicker.tsx
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import {
  IconButton,
  InputAdornment,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import I18n from "i18n-js";
import moment from "moment-timezone";
import { useRef, useState } from "react";
import { getTextFieldColorIndicatorSx } from "../TextField/TextField";
import { ColorIndicatorProps } from "../types";
export interface DatePickerProps
  extends ColorIndicatorProps,
    Omit<
      MuiDatePickerProps<string, string>,
      "value" | "onChange" | "renderInput"
    > {
  id?: string;
  valueFormat?: string;
  variant?: "outlined" | "standard" | "standard";
  label?: string;
  value?: any;
  isDirty?: boolean;
  clearable?: boolean;
  required?: boolean;
  onChange?: Function;
  placeholder?: string;
  textFieldProps?: Omit<TextFieldProps, "variant">;
  error?: string;
  helperText?: string;
  hideValue?: (value: any) => any;
  onBlur?: (evt: any) => void;
  renderInput?: (props: {
    props: any;
    value?: any;
    error?: any;
    helperText?: any;
    id?: any;
    clearable: boolean;
    setOpen: Function;
    clear: Function;
  }) => any;
}
export default function DatePicker({
  id,
  variant, // = "outlined",
  label: _label,
  valueFormat = "YYYY-MM-DD",
  value: rawValue,
  onChange,
  placeholder,
  textFieldProps,
  error,
  helperText,
  isDirty,
  inputFormat,
  clearable = true,
  required,
  indicatorColor,
  indicatorOpacity,
  renderInput,
  hideValue,
  onBlur,
  ...pickerProps
}: DatePickerProps) {
  const [open, setOpen] = useState<boolean>(false);
  const fieldRef = useRef<any>(null);
  function exportDate(d: any) {
    if (!d) {
      return null;
    }
    const v = moment(d);

    if (!v.isValid()) {
      return null;
    }
    return d.format(valueFormat);
  }
  function parseDate(d: any) {
    if (!d) {
      return null;
    }
    const v = moment(d);

    if (!v.isValid()) {
      return null;
    }
    return d;
  }
  const value = parseDate(rawValue);

  const handleChange = (newValue: any) => {
    onChange && onChange({ target: { value: exportDate(newValue) } });
  };

  const getValue = () => {
    return !value || value === "" ? null : value;
  };
  const theme = useTheme();

  const label = required ? (
    <span>
      {_label} <span style={{ color: "red" }}>*</span>
    </span>
  ) : (
    _label
  );
  const mask = I18n.t("dateFormat.date").replace(/[A-z]/g, "_");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MuiDatePicker
        //calendars={1}
        open={open}
        onClose={() => setOpen(false)}
        //clearable
        label={label}
        value={getValue()}
        onChange={handleChange}
        mask={mask}
        inputFormat={inputFormat || I18n.t("dateFormat.date")}
        renderInput={props => {
          if (renderInput) {
            return renderInput({
              props,
              value,
              error,
              helperText,
              id,
              clearable,
              setOpen,
              clear: () => handleChange(null),
            });
          }
          if (hideValue && !showPassword) {
            return (
              <TextField
                {...props}
                inputProps={{
                  readOnly: true,
                  ...props.inputProps,
                  value: hideValue(props.inputProps?.value),
                  placeholder,
                }}
                label={label}
                onFocus={evt => {
                  setShowPassword(true);
                  setTimeout(() => {
                    if (fieldRef.current?.element) {
                      fieldRef.current?.element.focus();
                    }
                  }, 300);
                  props.onFocus && props.onFocus(evt);
                }}
                InputProps={
                  clearable
                    ? {
                        ...props.InputProps,
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            style={{ marginRight: -6 }}
                          >
                            {!!value && (
                              <IconButton
                                disabled={props.disabled}
                                onClick={() => handleChange(null)}
                                sx={{ color: theme.palette.text.secondary }}
                                size="small"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              disabled={props.disabled}
                              onClick={() => setOpen(true)}
                              size="small"
                            >
                              <EventIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : props.InputProps
                }
                sx={{
                  ...getTextFieldColorIndicatorSx(id, {
                    indicatorColor,
                    indicatorOpacity,
                  }),
                  ...props.sx,
                }}
                id={id}
                variant={variant}
                helperText={error || helperText}
                error={error ? true : false}
                {...textFieldProps}
                value={hideValue(props.inputProps?.value)}
              />
            );
          }
          return (
            <TextField
              ref={fieldRef}
              {...props}
              onBlur={evt => {
                if (showPassword && hideValue) {
                  setShowPassword(false);
                }
                props.onBlur && props.onBlur(evt);
                onBlur && onBlur(evt);
              }}
              sx={{
                ...getTextFieldColorIndicatorSx(id, {
                  indicatorColor,
                  indicatorOpacity,
                }),
                ...props.sx,
              }}
              id={id}
              variant={variant}
              helperText={error || helperText}
              error={error ? true : false}
              inputProps={{
                ...props.inputProps,
                placeholder,
              }}
              InputProps={
                clearable
                  ? {
                      ...props.InputProps,
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          style={{ marginRight: -6 }}
                        >
                          {!!value && (
                            <IconButton
                              disabled={props.disabled}
                              onClick={() => handleChange(null)}
                              sx={{ color: theme.palette.text.secondary }}
                              size="small"
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            disabled={props.disabled}
                            onClick={() => setOpen(true)}
                            size="small"
                          >
                            <EventIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  : props.InputProps
              }
              {...textFieldProps}
            />
          );
        }}
        {...pickerProps}
      />
    </LocalizationProvider>
  );
}

// FILECONTENT_END src/common/components/Inputs/DatePicker/DatePicker.tsx
